import { c as _c } from "react/compiler-runtime";
import { Check, ChevronsUpDown, LucideIcon, Search } from "lucide-react";
import React, { useDeferredValue, useState, useEffect } from "react";
import { Button } from "@/src/components/shadcn/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/src/components/shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/shadcn/ui/popover";
import { type EntityMatcherType, type EntityType, useEntityMatcherConfig } from "@/src/components/tasks/intake-sections/matcher-utils";
import { useExtractionAnnotationPopover } from "@/src/context/extraction-annotation-popover-context";
import { logger } from "@/src/lib/logging/logger";
import type { PayerWeighting } from "@/src/lib/payer-search";
import { cn } from "@/src/lib/shadcn-utils";
interface MatcherCommandItemProps {
  value: string;
  label: string;
  isSelected: boolean;
  onSelect: () => void;
  icon: LucideIcon;
  badge?: React.ReactNode;
  className?: string;
}
const MatcherCommandItem = t0 => {
  const $ = _c(20);
  const {
    value,
    icon: Icon,
    label,
    isSelected,
    onSelect,
    className,
    badge
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex items-center", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isSelected ? "opacity-100" : "opacity-0";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("mr-1 h-3 w-3 shrink-0", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <Check className={t3} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== Icon) {
    t5 = <Icon className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
    $[6] = Icon;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== label) {
    t6 = <span className="truncate mr-2">{label}</span>;
    $[8] = label;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== badge || $[11] !== t6) {
    t7 = <div className="flex items-center w-full">{t6}{badge}</div>;
    $[10] = badge;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== onSelect || $[14] !== t1 || $[15] !== t4 || $[16] !== t5 || $[17] !== t7 || $[18] !== value) {
    t8 = <CommandItem value={value} onSelect={onSelect} className={t1}>{t4}{t5}{t7}</CommandItem>;
    $[13] = onSelect;
    $[14] = t1;
    $[15] = t4;
    $[16] = t5;
    $[17] = t7;
    $[18] = value;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
interface EntityMatcherProps<T extends EntityMatcherType> {
  // Entity type - determines configuration and external data sources
  type: EntityType;

  // Entities that are already persisted in the DB
  existingEntities?: T[] | undefined;
  isLoadingExistingEntities?: boolean;

  // Entity rendered as selected in the <Command> UI
  entity: T | null;
  onEntitySelected: (entity: T | null) => void;

  // Create new option
  showCreateNewOption?: boolean;
  weighting?: PayerWeighting;

  // Not listed option
  showNotListedOption?: boolean;
  isNotListedSelected?: boolean;
  onSelectNotListed?: () => void;

  // Match results from block matching (e.g., patient matching)
  // Structure: { entity: T | Partial<T>; confidence: number }[]
  matchResults?: Array<{
    entity: T | Partial<T>;
    confidence: number;
  }>;

  // Tab index for keyboard navigation
  tabIndex?: number;

  // Disable external search - if true, will not use external data sources (e.g., NPI, geocoding)
  disableExternalSearch?: boolean;
}
function EntityMatcher(t0) {
  const $ = _c(126);
  const {
    type,
    existingEntities,
    isLoadingExistingEntities,
    entity,
    onEntitySelected,
    showCreateNewOption: t1,
    weighting,
    matchResults,
    tabIndex,
    disableExternalSearch: t2,
    showNotListedOption: t3,
    isNotListedSelected: t4,
    onSelectNotListed
  } = t0;
  const showCreateNewOption = t1 === undefined ? true : t1;
  const disableExternalSearch = t2 === undefined ? false : t2;
  const showNotListedOption = t3 === undefined ? false : t3;
  const isNotListedSelected = t4 === undefined ? false : t4;
  const openExtractionAnnotation = useExtractionAnnotationPopover();
  const {
    search,
    displayConfig,
    externalDataSources,
    generateInitialSearchQuery
  } = useEntityMatcherConfig(type);
  let t5;
  if ($[0] !== disableExternalSearch || $[1] !== externalDataSources) {
    t5 = disableExternalSearch ? [] : externalDataSources;
    $[0] = disableExternalSearch;
    $[1] = externalDataSources;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const filteredExternalDataSources = t5;
  let t6;
  if ($[3] !== entity || $[4] !== generateInitialSearchQuery) {
    t6 = entity && generateInitialSearchQuery ? generateInitialSearchQuery(entity) : "";
    $[3] = entity;
    $[4] = generateInitialSearchQuery;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const initialSearchQuery = t6;
  const [open, setOpen] = useState(false);
  const [commandSelection, setCommandSelection] = useState(null);
  const [searchInput, setSearchInput] = useState(initialSearchQuery);
  let t7;
  if ($[6] !== searchInput) {
    t7 = searchInput.trim();
    $[6] = searchInput;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const deferredSearch = useDeferredValue(t7);
  const entityId = entity?.id;
  const isCreateNewSelected = showCreateNewOption && !entityId;
  const entities = existingEntities ?? [];
  const searchResults = deferredSearch || weighting && weighting.length > 0 ? search(entities, deferredSearch || "", weighting) : entities.slice(0, 20);
  let t8;
  if ($[8] !== entityId || $[9] !== matchResults || $[10] !== searchInput) {
    t8 = new Set(!searchInput && matchResults ? matchResults.filter(match => {
      const matchEntity = match.entity as T;
      return matchEntity && "id" in matchEntity && matchEntity.id && matchEntity.id !== entityId;
    }).map(_temp).filter(_temp2) : []);
    $[8] = entityId;
    $[9] = matchResults;
    $[10] = searchInput;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const matchResultIds = t8;
  let t9;
  if ($[12] !== entityId || $[13] !== matchResultIds) {
    t9 = candidate => {
      if (entityId && candidate.id === entityId) {
        return false;
      }
      if (matchResultIds.has(candidate.id)) {
        return false;
      }
      return true;
    };
    $[12] = entityId;
    $[13] = matchResultIds;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const filteredEntities = searchResults.filter(t9);
  const firstExisting = filteredEntities[0];
  let t10;
  if ($[15] !== filteredExternalDataSources) {
    t10 = filteredExternalDataSources.flatMap(_temp3);
    $[15] = filteredExternalDataSources;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const firstExternal = t10[0];
  const defaultCommandValue = firstExisting?.id ?? firstExternal?.id ?? (showCreateNewOption ? "create-new" : "");
  const selectedCommandValue = commandSelection?.query === deferredSearch ? commandSelection.value : defaultCommandValue;
  let t11;
  if ($[17] !== entityId || $[18] !== initialSearchQuery) {
    t11 = nextOpen => {
      if (nextOpen && !entityId && initialSearchQuery) {
        setSearchInput(initialSearchQuery);
      }
      setOpen(nextOpen);
    };
    $[17] = entityId;
    $[18] = initialSearchQuery;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleOpenChange = t11;
  let t12;
  if ($[20] !== filteredExternalDataSources || $[21] !== onEntitySelected || $[22] !== openExtractionAnnotation) {
    t12 = selectedEntity => {
      onEntitySelected(selectedEntity);
      setOpen(false);
      openExtractionAnnotation?.();
      setSearchInput("");
      setCommandSelection(null);
      filteredExternalDataSources.forEach(_temp4);
    };
    $[20] = filteredExternalDataSources;
    $[21] = onEntitySelected;
    $[22] = openExtractionAnnotation;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleSelectEntity = t12;
  let t13;
  bb0: {
    if (!matchResults || matchResults.length === 0 || searchInput) {
      let t14;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = [];
        $[24] = t14;
      } else {
        t14 = $[24];
      }
      t13 = t14;
      break bb0;
    }
    let t14;
    if ($[25] !== displayConfig || $[26] !== entityId || $[27] !== handleSelectEntity || $[28] !== matchResults) {
      let t15;
      if ($[30] !== entityId) {
        t15 = match_1 => {
          const matchEntity_1 = match_1.entity as T;
          return matchEntity_1 && "id" in matchEntity_1 && matchEntity_1.id && matchEntity_1.id !== entityId;
        };
        $[30] = entityId;
        $[31] = t15;
      } else {
        t15 = $[31];
      }
      let t16;
      if ($[32] !== displayConfig || $[33] !== entityId || $[34] !== handleSelectEntity) {
        t16 = match_2 => {
          const matchEntity_2 = match_2.entity as T;
          if (!matchEntity_2 || !("id" in matchEntity_2) || !matchEntity_2.id) {
            return null;
          }
          return <MatcherCommandItem key={matchEntity_2.id} value={matchEntity_2.id} icon={displayConfig.entityIcon} label={displayConfig.getDisplayName(matchEntity_2)} isSelected={entityId === matchEntity_2.id} onSelect={() => handleSelectEntity(matchEntity_2)} />;
        };
        $[32] = displayConfig;
        $[33] = entityId;
        $[34] = handleSelectEntity;
        $[35] = t16;
      } else {
        t16 = $[35];
      }
      t14 = matchResults.filter(t15).map(t16).filter(_temp5);
      $[25] = displayConfig;
      $[26] = entityId;
      $[27] = handleSelectEntity;
      $[28] = matchResults;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    t13 = t14;
  }
  const validMatchResults = t13;
  let t14;
  if ($[36] !== deferredSearch || $[37] !== disableExternalSearch || $[38] !== externalDataSources) {
    t14 = () => {
      if (disableExternalSearch) {
        return;
      }
      externalDataSources.forEach(source_1 => {
        if (source_1.shouldShow(deferredSearch)) {
          ;
          try {
            source_1.search(deferredSearch);
          } catch (t15) {
            const error = t15;
            logger.error("Error searching external data source", {
              error
            });
          }
        } else {
          source_1.search("");
        }
      });
    };
    $[36] = deferredSearch;
    $[37] = disableExternalSearch;
    $[38] = externalDataSources;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  useEffect(t14, [deferredSearch, disableExternalSearch, externalDataSources, filteredEntities.length]);
  if (isLoadingExistingEntities) {
    let t15;
    if ($[40] !== displayConfig.entityIcon) {
      t15 = <displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />;
      $[40] = displayConfig.entityIcon;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    let t16;
    if ($[42] !== displayConfig.entityListTitle) {
      t16 = displayConfig.entityListTitle.toLowerCase();
      $[42] = displayConfig.entityListTitle;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    const t17 = `Loading ${t16}...`;
    let t18;
    if ($[44] !== t17) {
      t18 = <span className="truncate">{t17}</span>;
      $[44] = t17;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] !== t15 || $[47] !== t18) {
      t19 = <div className="min-w-0 flex-1 flex items-center">{t15}{t18}</div>;
      $[46] = t15;
      $[47] = t18;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    let t20;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
      $[49] = t20;
    } else {
      t20 = $[49];
    }
    let t21;
    if ($[50] !== t19) {
      t21 = <div className="mb-2 mt-2 w-full"><Button variant="outline" disabled={true} className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm p-2 border-accent">{t19}{t20}</Button></div>;
      $[50] = t19;
      $[51] = t21;
    } else {
      t21 = $[51];
    }
    return t21;
  }
  const t15 = "mb-2 mt-2 w-full";
  const T0 = Popover;
  let t16;
  if ($[52] !== displayConfig || $[53] !== entity || $[54] !== isNotListedSelected || $[55] !== showCreateNewOption) {
    t16 = isNotListedSelected ? <><displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">Not Listed</span></> : !entity ? <>{showCreateNewOption ? <><displayConfig.createNewIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{displayConfig.createNewTitle}</span></> : <><Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{displayConfig.placeholder}</span></>}</> : entity ? <><displayConfig.entityIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" /><span className="truncate">{displayConfig.getDisplayName(entity)}</span><div className="ml-auto">{displayConfig?.renderBadge?.(entity)}</div></> : null;
    $[52] = displayConfig;
    $[53] = entity;
    $[54] = isNotListedSelected;
    $[55] = showCreateNewOption;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== t16) {
    t17 = <div className="min-w-0 flex-1 flex items-center">{t16}</div>;
    $[57] = t16;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== open || $[61] !== t17 || $[62] !== tabIndex) {
    t19 = <PopoverTrigger asChild={true}><Button variant="outline" role="combobox" aria-expanded={open} tabIndex={tabIndex} className="w-full flex items-center justify-between rounded-md shadow-md bg-input text-sm hover:bg-accent p-2 border-accent">{t17}{t18}</Button></PopoverTrigger>;
    $[60] = open;
    $[61] = t17;
    $[62] = tabIndex;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  const T1 = PopoverContent;
  const t20 = "w-[var(--radix-popover-trigger-width)] min-w-[400px] p-0 border-muted/50 rounded-sm shadow-sm bg-popover";
  const T2 = Command;
  const t21 = "w-full";
  const t22 = false;
  let t23;
  if ($[64] !== deferredSearch) {
    t23 = value => {
      setCommandSelection({
        query: deferredSearch,
        value
      });
    };
    $[64] = deferredSearch;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== open) {
    t24 = value_0 => {
      setSearchInput(value_0);
      if (!open) {
        setOpen(true);
      }
    };
    $[66] = open;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== displayConfig.placeholder || $[69] !== searchInput || $[70] !== t24) {
    t25 = <CommandInput placeholder={displayConfig.placeholder} value={searchInput} onValueChange={t24} />;
    $[68] = displayConfig.placeholder;
    $[69] = searchInput;
    $[70] = t24;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  const T3 = CommandList;
  let t26;
  if ($[72] !== displayConfig || $[73] !== entity || $[74] !== handleSelectEntity || $[75] !== searchInput) {
    t26 = entity?.id && !searchInput && <CommandGroup heading="Currently selected"><MatcherCommandItem key={entity.id} value={entity.id} icon={displayConfig.entityIcon} label={displayConfig.getDisplayName(entity)} isSelected={true} onSelect={() => handleSelectEntity(entity)} badge={displayConfig.renderBadge && displayConfig.renderBadge(entity)} /></CommandGroup>;
    $[72] = displayConfig;
    $[73] = entity;
    $[74] = handleSelectEntity;
    $[75] = searchInput;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== validMatchResults) {
    t27 = validMatchResults.length > 0 && <CommandGroup heading="Potential matches">{validMatchResults}</CommandGroup>;
    $[77] = validMatchResults;
    $[78] = t27;
  } else {
    t27 = $[78];
  }
  const t28 = existingEntities?.length ? <CommandGroup heading={displayConfig.entityListTitle}>{filteredEntities.length ? <>{filteredEntities.map(existingEntity => <MatcherCommandItem key={existingEntity.id} value={existingEntity.id} icon={displayConfig.entityIcon} label={displayConfig.getDisplayName(existingEntity)} isSelected={entity?.id === existingEntity.id} onSelect={() => handleSelectEntity(existingEntity as T)} badge={displayConfig.renderBadge && displayConfig.renderBadge(existingEntity)} />)}</> : <div className="px-6 py-1.5 text-sm">No matching existing {displayConfig.entityListTitle.toLowerCase()} found.</div>}</CommandGroup> : null;
  let t29;
  if ($[79] !== deferredSearch || $[80] !== displayConfig || $[81] !== entity?.id || $[82] !== filteredExternalDataSources || $[83] !== handleSelectEntity) {
    let t30;
    if ($[85] !== deferredSearch || $[86] !== displayConfig || $[87] !== entity?.id || $[88] !== handleSelectEntity) {
      t30 = (source_2, index) => {
        const sourceResults = source_2.results;
        const shouldShow = source_2.shouldShow(deferredSearch);
        const title = !source_2.isLoading && !source_2.error && sourceResults?.length ? source_2.groupTitle + " (" + sourceResults.length + " results)" : source_2.groupTitle;
        if (!shouldShow) {
          return null;
        }
        return <CommandGroup key={`external-${index}`} heading={title}>{source_2.isLoading ? <div className="px-6 py-1.5 text-sm">Searching...</div> : source_2.error ? <div className="px-6 py-1.5 text-sm">{source_2.error}</div> : sourceResults?.length ? sourceResults.map(externalEntity => <MatcherCommandItem key={`external-${index}-${externalEntity.id}`} value={externalEntity.id} icon={source_2.getIcon(externalEntity)} label={source_2.getDisplayName(externalEntity)} isSelected={entity?.id === externalEntity.id} onSelect={() => handleSelectEntity(externalEntity as T)} badge={displayConfig?.renderBadge?.(externalEntity)} />) : <div className="px-6 py-1.5 text-sm">No matching {source_2.groupTitle} found.</div>}</CommandGroup>;
      };
      $[85] = deferredSearch;
      $[86] = displayConfig;
      $[87] = entity?.id;
      $[88] = handleSelectEntity;
      $[89] = t30;
    } else {
      t30 = $[89];
    }
    t29 = filteredExternalDataSources.map(t30);
    $[79] = deferredSearch;
    $[80] = displayConfig;
    $[81] = entity?.id;
    $[82] = filteredExternalDataSources;
    $[83] = handleSelectEntity;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[90] !== displayConfig.entityIcon || $[91] !== isNotListedSelected || $[92] !== onSelectNotListed || $[93] !== openExtractionAnnotation || $[94] !== showNotListedOption) {
    t30 = showNotListedOption && <CommandGroup><MatcherCommandItem key="not-listed" value="not-listed" icon={displayConfig.entityIcon} label="Not Listed" isSelected={isNotListedSelected} onSelect={() => {
        setOpen(false);
        onSelectNotListed?.();
        openExtractionAnnotation?.();
      }} /></CommandGroup>;
    $[90] = displayConfig.entityIcon;
    $[91] = isNotListedSelected;
    $[92] = onSelectNotListed;
    $[93] = openExtractionAnnotation;
    $[94] = showNotListedOption;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  let t31;
  if ($[96] !== displayConfig.createNewIcon || $[97] !== displayConfig.createNewTitle || $[98] !== entity || $[99] !== handleSelectEntity || $[100] !== isCreateNewSelected || $[101] !== showCreateNewOption) {
    t31 = showCreateNewOption && <CommandGroup heading="Create"><MatcherCommandItem key="create-new" value="create-new" icon={displayConfig.createNewIcon} label={displayConfig.createNewTitle} isSelected={!entity || isCreateNewSelected} onSelect={() => {
        if (isCreateNewSelected) {
          setOpen(false);
          return;
        }
        handleSelectEntity(null);
      }} /></CommandGroup>;
    $[96] = displayConfig.createNewIcon;
    $[97] = displayConfig.createNewTitle;
    $[98] = entity;
    $[99] = handleSelectEntity;
    $[100] = isCreateNewSelected;
    $[101] = showCreateNewOption;
    $[102] = t31;
  } else {
    t31 = $[102];
  }
  let t32;
  if ($[103] !== T3 || $[104] !== t26 || $[105] !== t27 || $[106] !== t28 || $[107] !== t29 || $[108] !== t30 || $[109] !== t31) {
    t32 = <T3>{t26}{t27}{t28}{t29}{t30}{t31}</T3>;
    $[103] = T3;
    $[104] = t26;
    $[105] = t27;
    $[106] = t28;
    $[107] = t29;
    $[108] = t30;
    $[109] = t31;
    $[110] = t32;
  } else {
    t32 = $[110];
  }
  let t33;
  if ($[111] !== T2 || $[112] !== selectedCommandValue || $[113] !== t23 || $[114] !== t25 || $[115] !== t32) {
    t33 = <T2 className={t21} shouldFilter={t22} value={selectedCommandValue} onValueChange={t23}>{t25}{t32}</T2>;
    $[111] = T2;
    $[112] = selectedCommandValue;
    $[113] = t23;
    $[114] = t25;
    $[115] = t32;
    $[116] = t33;
  } else {
    t33 = $[116];
  }
  let t34;
  if ($[117] !== T1 || $[118] !== t33) {
    t34 = <T1 className={t20}>{t33}</T1>;
    $[117] = T1;
    $[118] = t33;
    $[119] = t34;
  } else {
    t34 = $[119];
  }
  let t35;
  if ($[120] !== T0 || $[121] !== handleOpenChange || $[122] !== open || $[123] !== t19 || $[124] !== t34) {
    t35 = <div className={t15}><T0 open={open} onOpenChange={handleOpenChange}>{t19}{t34}</T0></div>;
    $[120] = T0;
    $[121] = handleOpenChange;
    $[122] = open;
    $[123] = t19;
    $[124] = t34;
    $[125] = t35;
  } else {
    t35 = $[125];
  }
  return t35;
}
function _temp5(item) {
  return item !== null;
}
function _temp4(source_0) {
  source_0.search("");
}
function _temp3(source) {
  return source.results;
}
function _temp2(id) {
  return id !== null;
}
function _temp(match_0) {
  const matchEntity_0 = match_0.entity as T;
  return matchEntity_0 && "id" in matchEntity_0 ? matchEntity_0.id : null;
}
export { EntityMatcher };